import { basename } from 'node:path';
import {
  resolveExportedPath,
  type SessionExportBundle,
  type SessionExportSummary,
} from '@gitlab-lsp/workflow-api';
import { sliceAtCodePoint } from '../utils/slice_at_code_point';

export interface ImportedSessionLimits {
  listItems: number;
  itemChars: number;
  messageChars: number;
}

/** Every list and every interpolated string is capped, so the block stays bounded. Retuned by issue 2804. */
export const IMPORTED_SESSION_LIMITS: ImportedSessionLimits = {
  listItems: 12,
  itemChars: 240,
  messageChars: 1_000,
};

const NOTE =
  'Summary of an earlier session the user imported. Context only: that conversation was not replayed, the user cannot see its transcript in this session, and the repository may have changed since. Ask rather than assume anything not stated here.';

const MISMATCH_NOTE = 'Paths below were anchored to a different workspace and may not exist here.';

// Bundles can arrive from another machine, so content is untrusted: unescaped,
// a `</imported_session>` breaks out of the block and a `"` out of an attribute.
const escapeXml = (text: string): string =>
  text
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');

// Caps the escaped string, not the raw one: `"` becomes `&quot;`, so capping
// first lets a bundle of XML-special characters expand ~6x past the budget.
const truncateEscaped = (escaped: string, maxChars: number): string => {
  if (escaped.length <= maxChars) return escaped;

  const cut = sliceAtCodePoint(escaped, maxChars);
  const lastEntity = cut.lastIndexOf('&');
  // Snap back before a half-written entity rather than emit `&qu`.
  const safe =
    lastEntity !== -1 && !cut.slice(lastEntity).includes(';') ? cut.slice(0, lastEntity) : cut;

  return `${safe}…`;
};

interface Scrubber {
  (text: string, maxChars: number): string;
}

// Re-anchoring must precede the cap: it lengthens the string.
const makeScrubber =
  (workspaceRoot: string): Scrubber =>
  (text, maxChars) =>
    truncateEscaped(escapeXml(resolveExportedPath(text, workspaceRoot)), maxChars);

const renderList = (
  tag: string,
  itemTag: string,
  values: string[],
  scrub: Scrubber,
  limits: ImportedSessionLimits,
): string[] => {
  if (values.length === 0) return [];

  const shown = values.slice(0, limits.listItems);
  const omitted = values.length - shown.length;
  const openTag = omitted > 0 ? `<${tag} truncated_count="${omitted}">` : `<${tag}>`;

  return [
    openTag,
    ...shown.map((value) => `<${itemTag}>${scrub(value, limits.itemChars)}</${itemTag}>`),
    `</${tag}>`,
  ];
};

// Capped as well as escaped: attribute values are bundle-controlled too, so
// without this an oversized id or tool name bypasses the size budget entirely.
const renderAttributes = (
  attributes: Record<string, string | number | undefined>,
  maxChars: number,
): string =>
  Object.entries(attributes)
    .filter(([, value]) => value !== undefined && value !== '')
    .map(([name, value]) => ` ${name}="${truncateEscaped(escapeXml(String(value)), maxChars)}"`)
    .join('');

const renderSessionLine = (
  bundle: SessionExportBundle,
  scrub: Scrubber,
  limits: ImportedSessionLimits,
) => {
  const attributes = renderAttributes(
    {
      id: bundle.session.id,
      flow: bundle.session.flow,
      exported_at: bundle.exportedAt,
    },
    limits.itemChars,
  );
  const title = bundle.session.title ? scrub(bundle.session.title, limits.messageChars) : '';
  return `<session${attributes}>${title}</session>`;
};

const renderStats = (summary: SessionExportSummary, limits: ImportedSessionLimits): string =>
  `<stats${renderAttributes(
    {
      entries: summary.entryCount,
      tool_executions: summary.toolExecutionCount,
      first_entry_at: summary.firstEntryAt,
      last_entry_at: summary.lastEntryAt,
    },
    limits.itemChars,
  )} />`;

const renderErrors = (
  summary: SessionExportSummary,
  scrub: Scrubber,
  limits: ImportedSessionLimits,
): string[] => {
  if (summary.unresolvedErrors.length === 0) return [];

  const shown = summary.unresolvedErrors.slice(0, limits.listItems);
  const omitted = summary.unresolvedErrors.length - shown.length;

  return [
    omitted > 0 ? `<unresolved_errors truncated_count="${omitted}">` : '<unresolved_errors>',
    ...shown.map(
      ({ tool, message }) =>
        `<error${renderAttributes({ tool }, limits.itemChars)}>${scrub(message, limits.itemChars)}</error>`,
    ),
    '</unresolved_errors>',
  ];
};

// `deriveSessionSummary` emits at most one, but that is a producer invariant the
// schema does not enforce, so cap it like any other list.
const renderPending = (summary: SessionExportSummary, limits: ImportedSessionLimits): string[] => {
  if (summary.pendingToolCalls.length === 0) return [];

  const shown = summary.pendingToolCalls.slice(0, limits.listItems);
  const omitted = summary.pendingToolCalls.length - shown.length;

  return [
    omitted > 0 ? `<pending_tool_calls truncated_count="${omitted}">` : '<pending_tool_calls>',
    ...shown.map(({ tool }) => `<call${renderAttributes({ tool }, limits.itemChars)} />`),
    '</pending_tool_calls>',
  ];
};

/**
 * Renders a bundle's summary as one `agent_user_environment` payload.
 */
export function renderImportedSessionContext(
  bundle: SessionExportBundle,
  {
    workspaceRoot,
    limits = IMPORTED_SESSION_LIMITS,
  }: {
    workspaceRoot: string;
    limits?: ImportedSessionLimits;
  },
): string {
  const scrub = makeScrubber(workspaceRoot);
  const { summary } = bundle;
  const currentRootName = basename(workspaceRoot);

  const lines = [
    '<imported_session>',
    `<note>${NOTE}</note>`,
    renderSessionLine(bundle, scrub, limits),
  ];

  if (currentRootName !== bundle.workspace.rootName) {
    lines.push(
      `<workspace_mismatch${renderAttributes(
        {
          exported_from: bundle.workspace.rootName,
          current: currentRootName,
        },
        limits.itemChars,
      )}>${MISMATCH_NOTE}</workspace_mismatch>`,
    );
  }

  lines.push(
    renderStats(summary, limits),
    ...renderList('user_goals', 'goal', summary.userGoals, scrub, limits),
    ...renderList('files_changed', 'file', summary.filesChanged, scrub, limits),
    ...renderList('files_read', 'file', summary.filesRead, scrub, limits),
    ...renderList('commands_run', 'command', summary.commandsRun, scrub, limits),
    ...renderErrors(summary, scrub, limits),
    ...renderPending(summary, limits),
  );

  if (summary.text) {
    lines.push(`<recap>${scrub(summary.text, limits.messageChars)}</recap>`);
  }

  if (summary.finalAgentMessage) {
    lines.push(
      `<final_agent_message>${scrub(summary.finalAgentMessage, limits.messageChars)}</final_agent_message>`,
    );
  }

  lines.push('</imported_session>');

  return lines.join('\n');
}
